import { Injectable } from '@angular/core';

/**
 * Interface representing a cached item with its value and optional expiry timestamp
 */
interface CacheItem {
    value: any;
    expiry?: number;
}

/**
 * Service that provides in-memory caching functionality with optional expiration.
 * Used to temporarily store and retrieve data to avoid unnecessary API calls.
 */
@Injectable({
    providedIn: 'root',
})
export class MemoryCacheService {
    // Map to store cached items with their keys
    private cache = new Map<string, CacheItem>();

    constructor() {}

    /**
     * Stores a value in the cache with an optional retention time
     * @param key Unique identifier for the cached item
     * @param value The data to cache
     * @param retentionMinutes Optional time in minutes after which the item expires
     */
    set(key: string, value: any, retentionMinutes?: number): void {
        const expiry = retentionMinutes ? Date.now() + retentionMinutes * 60 * 1000 : null;
        this.cache.set(key, { value, expiry });
    }

    /**
     * Retrieves a value from the cache if it exists and hasn't expired
     * @param key The identifier of the cached item
     * @returns The cached value or null if not found/expired
     */
    get(key: string): any {
        const item = this.cache.get(key);
        if (!item) return null;

        if (item.expiry && Date.now() > item.expiry) {
            this.cache.delete(key);
            return null;
        }

        return item.value;
    }

    /**
     * Checks if a valid (non-expired) item exists in the cache
     * @param key The identifier to check
     */
    has(key: string): boolean {
        return this.get(key) !== null;
    }

    /**
     * Removes a specific item from the cache
     * @param key The identifier of the item to remove
     */
    delete(key: string): void {
        this.cache.delete(key);
    }

    /**
     * Removes all items from the cache
     */
    clear(): void {
        this.cache.clear();
    }
}
